import React, { useEffect, useRef, useState, useCallback } from "react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import { fetchJson } from "../../api";
import ParallelismiPanel from "./ParallelismiPanel";
import StopLineePanel from "./StopLineePanel";
import EpisodeAssignModal from "./EpisodeAssignModal";
import { useParams, useNavigate } from "react-router-dom";
import SquadPanel from "./SquadPanel";
import AddLineModal from "./AddLineModal";


// Genera lista di offset relativi (in px) dei giorni weekend dentro un blocco
function weekendOffsetsInBlock(startDateISO, endDateISO, allowSat, allowSun, dayW) {
  const offsets = [];
  const start = new Date(startDateISO + 'T00:00:00Z');
  const end = new Date(endDateISO + 'T00:00:00Z');
  let cur = new Date(start.getTime());
  let i = 0;
  while (cur <= end) {
    const dow = cur.getUTCDay(); // 0=dom, 6=sab
    const isSat = dow === 6;
    const isSun = dow === 0;
    if ((isSat && !allowSat) || (isSun && !allowSun)) {
      offsets.push(i * dayW);
    }
    cur.setUTCDate(cur.getUTCDate() + 1);
    i++;
  }
  return offsets;
}
const EP_COLORS = [
  { bg:"rgba(59,130,246,0.20)",  border:"rgba(59,130,246,0.60)",  text:"#93c5fd" },  // blu
  { bg:"rgba(239,68,68,0.20)",   border:"rgba(239,68,68,0.60)",   text:"#fca5a5" },  // rosso
  { bg:"rgba(52,211,153,0.20)",  border:"rgba(52,211,153,0.60)",  text:"#6ee7b7" },  // verde menta
  { bg:"rgba(212,165,80,0.20)",  border:"rgba(212,165,80,0.60)",  text:"#fcd34d" },  // oro
  { bg:"rgba(217,70,239,0.20)",  border:"rgba(217,70,239,0.60)",  text:"#e879f9" },  // viola brillante
  { bg:"rgba(249,115,22,0.20)",  border:"rgba(249,115,22,0.60)",  text:"#fb923c" },  // arancio
  { bg:"rgba(20,184,166,0.20)",  border:"rgba(20,184,166,0.60)",  text:"#5eead4" },  // teal
  { bg:"rgba(244,63,94,0.20)",   border:"rgba(244,63,94,0.60)",   text:"#fb7185" },  // rosa acceso
  { bg:"rgba(163,230,53,0.20)",  border:"rgba(163,230,53,0.60)",  text:"#bef264" },  // verde lime
  { bg:"rgba(139,92,246,0.20)",  border:"rgba(139,92,246,0.60)",  text:"#a78bfa" },  // indaco
  { bg:"rgba(234,179,8,0.20)",   border:"rgba(234,179,8,0.60)",   text:"#fde047" },  // giallo
  { bg:"rgba(236,72,153,0.20)",  border:"rgba(236,72,153,0.60)",  text:"#f9a8d4" },  // fucsia
  { bg:"rgba(34,211,238,0.20)",  border:"rgba(34,211,238,0.60)",  text:"#67e8f9" },  // azzurro cielo
  { bg:"rgba(101,163,13,0.20)",  border:"rgba(101,163,13,0.60)",  text:"#84cc16" },  // verde oliva
  { bg:"rgba(251,113,133,0.22)", border:"rgba(251,113,133,0.60)", text:"#fda4af" },  // rosa chiaro
  { bg:"rgba(14,165,233,0.20)",  border:"rgba(14,165,233,0.60)",  text:"#38bdf8" },  // celeste
  { bg:"rgba(180,83,9,0.22)",    border:"rgba(180,83,9,0.60)",    text:"#f97316" },  // marrone arancio
  { bg:"rgba(99,102,241,0.20)",  border:"rgba(99,102,241,0.60)",  text:"#818cf8" },  // pervinca
  { bg:"rgba(4,120,87,0.22)",    border:"rgba(4,120,87,0.60)",    text:"#34d399" },  // verde scuro
  { bg:"rgba(190,18,60,0.20)",   border:"rgba(190,18,60,0.60)",   text:"#f43f5e" },  // cremisi
  { bg:"rgba(2,132,199,0.20)",   border:"rgba(2,132,199,0.60)",   text:"#0ea5e9" },  // blu oceano
  { bg:"rgba(124,58,237,0.20)",  border:"rgba(124,58,237,0.60)",  text:"#8b5cf6" },  // viola scuro
  { bg:"rgba(5,150,105,0.20)",   border:"rgba(5,150,105,0.60)",   text:"#10b981" },  // smeraldo
  { bg:"rgba(251,146,60,0.20)",  border:"rgba(251,146,60,0.60)",  text:"#fdba74" },  // pesca
];
const MONTHS_IT = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
const DAYS_IT   = ["Do","Lu","Ma","Me","Gi","Ve","Sa"];
const RISK_COLOR  = { green:"#4ade80", yellow:"#fbbf24", orange:"#fb923c", red:"#f87171" };
const RISK_BG     = { green:"rgba(74,222,128,0.08)", yellow:"rgba(251,191,36,0.08)", orange:"rgba(251,146,60,0.08)", red:"rgba(248,113,113,0.08)" };
const RISK_BORDER = { green:"rgba(74,222,128,0.30)", yellow:"rgba(251,191,36,0.30)", orange:"rgba(251,146,60,0.30)", red:"rgba(248,113,113,0.30)" };
const ROW_H=42, LABEL_W=220, HEADER_H=58;

function parseISO(s){ if(!s) return new Date(); const [y,m,d]=s.split("-").map(Number); return new Date(Date.UTC(y,m-1,d)); }
function fmtISO(d){ return d.toISOString().slice(0,10); }
function addDays(d,n){ const r=new Date(d); r.setUTCDate(r.getUTCDate()+n); return r; }
function daysBetween(a,b){ return Math.round((parseISO(b)-parseISO(a))/86400000); }
function todayISO(){ return fmtISO(new Date()); }

function BlockTooltip({ block, lanes = [], onClose, onRefresh, workingDays = [1,2,3,4,5] }) {
  const col = block ? EP_COLORS[block.episodeNumber ? (block.episodeNumber-1) % EP_COLORS.length : 0] : EP_COLORS[0];
  const [delta, setDelta] = React.useState(0);
  const [allowSat, setAllowSat] = React.useState(false);
  const [allowSun, setAllowSun] = React.useState(false);
  const [partLabel, setPartLabel] = React.useState("");
  const [saving, setSaving] = React.useState(false);
  const [msg, setMsg] = React.useState(null);
  const [moving, setMoving] = React.useState(false);
  const [moveD, setMoveD] = React.useState(1);
  const [showMoveToLine, setShowMoveToLine] = React.useState(false);
  const [targetLineId, setTargetLineId] = React.useState(null);
  const [movingLine, setMovingLine] = React.useState(false);

  React.useEffect(() => {
    if (block) {
      setAllowSat(!!block.allowSat);
      setAllowSun(!!block.allowSun);
      setPartLabel(block.partLabel || "");
      setDelta(0);
    }
  }, [block?.id]);
  async function handleMove(days) {
    setMoving(true); setMsg(null);
    try {
      // Calcola nuova startDate aggiungendo giorni lavorativi
      function addWorkDays(isoDate, n) {
        let d = new Date(isoDate + "T00:00:00Z");
        const step = n > 0 ? 1 : -1;
        let rem = Math.abs(n);
        while (rem > 0) {
          d.setUTCDate(d.getUTCDate() + step);
          const dow = d.getUTCDay();
          if (workingDays.includes(dow)) rem--;
        }
        return d.toISOString().slice(0,10);
      }
      const newStart = addWorkDays(block.startDate, days);
      await fetchJson(`/postplans/blocks/${block.id}/move`, {
        method:"PATCH",
        body: JSON.stringify({ startDate: newStart })
      });
      setMsg(`✓ Spostato di ${days > 0 ? '+' : ''}${days}gg`);
      setTimeout(() => { onRefresh && onRefresh(); onClose(); }, 600);
    } catch(e) {
      setMsg("Errore: " + e.message);
    } finally {
      setMoving(false);
    }
  }
  async function handleMoveToLine() {
    if (!targetLineId) return;
    setMovingLine(true); setMsg(null);
    try {
      await fetchJson(`/postplans/blocks/${block.id}/move-to-line`, {
        method: "PATCH",
        body: JSON.stringify({ lineId: Number(targetLineId) })
      });
      setMsg("✓ Episodio spostato");
      setTimeout(() => { onRefresh && onRefresh(); onClose(); }, 600);
    } catch(e) {
      setMsg("Errore: " + e.message);
    } finally {
      setMovingLine(false);
    }
  }
  if (!block) return null;
  const previewDur = Math.max(1, (block.durWork||1) + delta);

  async function handleApply() {
    setSaving(true); setMsg(null);
    try {
      const promises = [];
      if (delta !== 0) {
        promises.push(fetchJson(`/postplans/blocks/${block.id}/resize`, {
          method:"PATCH",
          body: JSON.stringify({ deltaDays: delta, allowSat, allowSun })
        }));
      }
      if (allowSat !== !!block.allowSat || allowSun !== !!block.allowSun) {
        promises.push(fetchJson(`/postplans/blocks/${block.id}/override`, {
          method:"PATCH",
          body: JSON.stringify({ allowSat, allowSun })
        }));
      }
      if (partLabel !== (block.partLabel||"")) {
        promises.push(fetchJson(`/postplans/blocks/${block.id}/note`, {
          method:"PATCH",
          body: JSON.stringify({ partLabel })
        }));
      }
      if (promises.length === 0) { setSaving(false); onClose(); return; }
      await Promise.all(promises);
      setMsg("✓ Salvato");
      setTimeout(() => { onRefresh && onRefresh(); onClose(); }, 1200);
    } catch(e) {
      setMsg("Errore: " + e.message);
    } finally {
      setSaving(false);
    }
  }

  const sep = { borderTop:"1px solid rgba(255,255,255,0.07)", margin:"12px 0" };
  const lbl = { fontSize:11, color:"rgba(232,230,223,0.45)", marginBottom:4, display:"block" };
  const row = { display:"flex", alignItems:"center", gap:8 };

  return (
    <div onClick={onClose} style={{ position:"fixed",inset:0,zIndex:1300,display:"flex",alignItems:"center",justifyContent:"center",background:"rgba(0,0,0,0.55)" }}>
      <div onClick={e=>e.stopPropagation()} style={{ background:"#1a1a26",border:`1px solid ${col.border}`,borderRadius:10,padding:"16px 20px",minWidth:280,maxWidth:340,boxShadow:"0 8px 32px rgba(0,0,0,0.6)" }}>

        {/* Header */}
        <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:12 }}>
          <div style={{ width:10,height:10,borderRadius:2,background:col.bg,border:`1px solid ${col.border}` }}/>
          <span style={{ color:col.text,fontWeight:600,fontSize:13 }}>EP.{block.episodeNumber} — {block.phaseName}</span>
          {block.isLocked && <span style={{ marginLeft:"auto",fontSize:10 }}>🔒</span>}
        </div>

        {/* Info */}
        <table style={{ width:"100%",fontSize:12,borderCollapse:"collapse",marginBottom:4 }}>
          <span style={lbl}>Sposta blocco</span>
          <div style={{...row, marginBottom:8}}>
            <button onClick={()=>handleMove(-moveD)} disabled={moving} style={{ background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:6,color:"#e8e6df",fontSize:12,padding:"4px 10px",cursor:"pointer" }}>◀ -{moveD}g</button>
            <div style={{ flex:1,display:"flex",alignItems:"center",justifyContent:"center",gap:6 }}>
              <button onClick={()=>setMoveD(d=>Math.max(1,d-1))} style={{ background:"none",border:"1px solid rgba(255,255,255,0.10)",borderRadius:4,color:"rgba(232,230,223,0.5)",fontSize:12,width:24,height:24,cursor:"pointer" }}>−</button>
              <span style={{ fontSize:12,color:"rgba(232,230,223,0.6)",minWidth:30,textAlign:"center" }}>{moveD}gg</span>
              <button onClick={()=>setMoveD(d=>Math.min(30,d+1))} style={{ background:"none",border:"1px solid rgba(255,255,255,0.10)",borderRadius:4,color:"rgba(232,230,223,0.5)",fontSize:12,width:24,height:24,cursor:"pointer" }}>+</button>
            </div>
            <button onClick={()=>handleMove(moveD)} disabled={moving} style={{ background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:6,color:"#e8e6df",fontSize:12,padding:"4px 10px",cursor:"pointer" }}>+{moveD}g ▶</button>
          </div>
          {[["Inizio",block.startDate],["Fine",block.endDate],["Durata lav.",`${block.durWork} gg`],block.lineLabel&&["Assegnato a",block.lineLabel]].filter(Boolean).map(([k,v])=>(
            <tr key={k}><td style={{ color:"rgba(232,230,223,0.45)",paddingBottom:3,paddingRight:12 }}>{k}</td><td style={{ color:"#e8e6df",paddingBottom:3 }}>{v}</td></tr>
          ))}
        </table>

        <div style={sep}/>

        {/* Parte puntata */}
        <div style={{ marginBottom:12 }}>
          <span style={lbl}>Parte puntata</span>
          <input value={partLabel} onChange={e=>setPartLabel(e.target.value)} placeholder="es. Confessionali, Cucina…"
            style={{ width:"100%",background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:6,color:"#e8e6df",fontSize:12,padding:"5px 8px",outline:"none" }}/>
        </div>

        {/* Modifica durata */}
        <div style={{ marginBottom:12 }}>
          <span style={lbl}>Modifica durata</span>
          <div style={row}>
            <button onClick={()=>setDelta(d=>Math.max(d-1,-(block.durWork-1)))} style={{ width:28,height:28,borderRadius:6,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",color:"#e8e6df",fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>−</button>
            <span style={{ flex:1,textAlign:"center",fontSize:13,fontWeight:600,color: delta===0?"rgba(232,230,223,0.6)":delta>0?"#4ade80":"#f87171" }}>
              {previewDur} gg {delta!==0&&<span style={{ fontSize:11 }}>({delta>0?"+":""}{delta})</span>}
            </span>
            <button onClick={()=>setDelta(d=>d+1)} style={{ width:28,height:28,borderRadius:6,background:"rgba(255,255,255,0.06)",border:"1px solid rgba(255,255,255,0.12)",color:"#e8e6df",fontSize:16,cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center" }}>+</button>
          </div>
        </div>

        {/* Cascade */}
        

        {/* Weekend */}
        <div style={{ marginBottom:4 }}>
          <span style={lbl}>Recupero straordinario</span>
          <div style={{ display:"flex",gap:16 }}>
            {[["Sabato",allowSat,setAllowSat],["Domenica",allowSun,setAllowSun]].map(([label,val,setter])=>(
              <label key={label} style={{ display:"flex",alignItems:"center",gap:6,fontSize:12,color:"rgba(232,230,223,0.6)",cursor:"pointer" }}>
                <input type="checkbox" checked={val} onChange={e=>setter(e.target.checked)} style={{ accentColor:"#d4a550" }}/>{label}
              </label>
            ))}
          </div>
        </div>

        <div style={sep}/>
        {/* Sposta su linea - temporaneamente disabilitato */}
        {/* Footer */}
        <div style={{ display:"flex",alignItems:"center",gap:8 }}>
          {msg && <span style={{ fontSize:11,color:msg.startsWith("✓")?"#4ade80":"#f87171",flex:1 }}>{msg}</span>}
          {!msg && <span style={{ flex:1 }}/>}
          <button onClick={onClose} style={{ background:"transparent",border:"1px solid rgba(255,255,255,0.12)",borderRadius:6,color:"rgba(232,230,223,0.45)",fontSize:11,padding:"4px 12px",cursor:"pointer" }}>Annulla</button>
          <button onClick={handleApply} disabled={saving} style={{ background:saving?"rgba(212,165,80,0.2)":"rgba(212,165,80,0.15)",border:"1px solid rgba(212,165,80,0.4)",borderRadius:6,color:"#d4a550",fontSize:11,fontWeight:600,padding:"4px 14px",cursor:saving?"not-allowed":"pointer" }}>{saving?"…":"Applica"}</button>
        </div>
      </div>


    </div>
  );
}
export default function TimelineView() {
  const { planId } = useParams();
  const nav = useNavigate();
  const [showSquad, setShowSquad] = useState(false);
  const [laneEdit, setLaneEdit] = useState(null);
  const [showSnapshots, setShowSnapshots] = useState(false);
  const [snapshots, setSnapshots] = useState([]);
  const [snapBusy, setSnapBusy] = useState(false);

  async function loadSnapshots() {
    try {
      const d = await fetchJson(`/postplans/${planId}/snapshots`);
      setSnapshots(d.snapshots || []);
    } catch(e) { console.error('snapshots error', e); }
  }

  async function restoreSnapshot(snapshotId) {
    if (!window.confirm('Ripristinare questo stato del piano? Lo stato attuale verrà salvato automaticamente prima del ripristino.')) return;
    setSnapBusy(true);
    try {
      await fetchJson(`/postplans/${planId}/snapshots/${snapshotId}/restore`, { method: 'POST' });
      setShowSnapshots(false);
      refreshPlan();
    } catch(e) { alert('Errore durante il ripristino: ' + e.message); }
    finally { setSnapBusy(false); }
  }

  async function createSnapshot() {
    setSnapBusy(true);
    try {
      await fetchJson(`/postplans/${planId}/snapshots`, { method: 'POST', body: JSON.stringify({ label: `Salvataggio manuale (${new Date().toLocaleString('it-IT', {day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})})` }) });
      await loadSnapshots();
    } catch(e) { alert('Errore: ' + e.message); }
    finally { setSnapBusy(false); }
  }
  const [showAddLine, setShowAddLine] = useState(false);
  const [showParallelismi, setShowParallelismi] = useState(false);
  const [showDatePanel, setShowDatePanel] = useState(false);
  const [showStopLinee, setShowStopLinee] = useState(false);
  const [showEpisodeAssign, setShowEpisodeAssign] = useState(false);
  const [dayW, setDayW] = useState(28);
  const [addLineForm, setAddLineForm] = useState({ phaseId: '', label: '', startDate: '', endDate: '' });
  const [addLineSaving, setAddLineSaving] = useState(false);
  const [showAddLineNew, setShowAddLineNew] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const [data, setData]       = useState(null);
  const refreshPlan = () => {
    const scrollX = gridRef.current?.scrollLeft ?? 0;
    const scrollY = gridRef.current?.scrollTop ?? 0;
    setLoading(true);
    fetchJson(`/postplans/${planId}/timeline`).then(d => {
      setData(d);
      setLoading(false);
      setTimeout(() => {
        if (gridRef.current) {
          gridRef.current.scrollLeft = scrollX;
          gridRef.current.scrollTop = scrollY;
        }
      }, 150);
    }).catch(() => setLoading(false));
  };
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);
  const [tooltip, setTooltip] = useState(null);
  const [filterEp, setFilterEp] = useState(null);
  const ganttRef  = useRef(null);
  const [exporting, setExporting] = useState(false);
  const gridRef   = useRef(null);

  const exportPDF = async (mode='poster') => {
    if (exporting) return;
    setExporting(true);
    try {
      const planName = data?.plan?.name || "piano";
      const payload = {
        planName,
        mode,
        dayW,
        episodes: episodes.map(e => ({ episodeNo: e.episodeNo, txDate: e.txDate })),
        lanes: lanes.map(l => ({ phaseId: l.phaseId, lineId: l.lineId, lineNo: l.lineNo, lineLabel: l.lineLabel, phaseName: l.phaseName, customStopFrom: l.customStopFrom, customStopTo: l.customStopTo })),
        blocks: blocksFlat.map(b => ({ phaseId: b.phaseId, lineId: b.lineId, episodeNumber: b.episodeNumber, startDate: b.startDate, endDate: b.endDate, assigneeLabel: b.assigneeLabel, partLabel: b.partLabel || '' })),
        days: (() => {
          const blockDates = blocksFlat.flatMap(b=>[b.startDate,b.endDate]);
          const txDates = episodes.filter(e=>e.txDate).map(e=>e.txDate.slice(0,10));
          const allDates = [...blockDates, ...txDates];
          if (!allDates.length) return days.map(d=>({iso:d.iso,isWeekend:d.isWeekend}));
          const minB = allDates.reduce((a,b)=>a<b?a:b);
          const maxB = allDates.reduce((a,b)=>a>b?a:b);
          return days.filter(d=>d.iso>=minB&&d.iso<=maxB).map(d=>({iso:d.iso,isWeekend:d.isWeekend}));
        })(),
      };
      const token = localStorage.getItem('token');
      const res = await fetch('/api/export-pdf', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Errore generazione PDF');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${planName.replace(/[^a-zA-Z0-9]/g,"_")}_${mode}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch(e) { console.error("Export PDF error:", e); alert("Errore export: " + e.message); }
    finally { setExporting(false); }
  };
  const labelsRef = useRef(null);

  useEffect(() => {
    if (!planId) return;
    setLoading(true); setError(null);
    fetch(`/api/postplans/${planId}/timeline`)
      .then(r => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [planId]);

  const syncScroll = useCallback(() => {
    if (gridRef.current && labelsRef.current) {
      labelsRef.current.scrollTop = gridRef.current.scrollTop;
      sessionStorage.setItem(`gantt_scrollX_${planId}`, gridRef.current.scrollLeft);
      sessionStorage.setItem(`gantt_scrollY_${planId}`, gridRef.current.scrollTop);
    }
  }, [planId]);

  useEffect(() => {
    if (!data || !gridRef.current) return;
    const allDates = data.blocksFlat.flatMap(b=>[b.startDate,b.endDate]);
    if (!allDates.length) return;
    const minISO = allDates.reduce((a,b)=>a<b?a:b);
    const startD = fmtISO(addDays(parseISO(minISO),-3));
    const off = daysBetween(startD, todayISO());
    const savedX = sessionStorage.getItem(`gantt_scrollX_${planId}`);
    const savedY = sessionStorage.getItem(`gantt_scrollY_${planId}`);
    if (savedX !== null || savedY !== null) {
      setTimeout(()=>{
        if(gridRef.current) {
          if (savedX !== null) gridRef.current.scrollLeft = Number(savedX);
          if (savedY !== null) gridRef.current.scrollTop = Number(savedY);
        }
      }, 80);
    } else if (off > 3) {
      setTimeout(()=>{ if(gridRef.current) gridRef.current.scrollLeft = Math.max(0,off*dayW-240); },80);
    }
  }, [data]);

  if (loading) return <div style={{ display:"flex",alignItems:"center",justifyContent:"center",height:300,background:"#0a0a0f",borderRadius:10,color:"rgba(232,230,223,0.4)",fontSize:13 }}>Caricamento piano…</div>;
  if (error)   return <div style={{ display:"flex",alignItems:"center",justifyContent:"center",height:300,background:"#0a0a0f",borderRadius:10,color:"#f87171",fontSize:13 }}>Errore: {error}</div>;
  if (!data)   return null;

  const { plan, episodes, blocksFlat } = data;

  const allDates = blocksFlat.flatMap(b=>[b.startDate,b.endDate]);
  episodes.forEach(e=>{ allDates.push(e.txDate); if(e.metrics?.deliveryDate) allDates.push(e.metrics.deliveryDate); });
  const minISO = allDates.reduce((a,b)=>a<b?a:b);
  const maxISO = allDates.reduce((a,b)=>a>b?a:b);
  const startD = fmtISO(addDays(parseISO(minISO),-3));
  const endD   = fmtISO(addDays(parseISO(maxISO),12));
  const totalDays = daysBetween(startD,endD)+1;
  const totalW    = totalDays*dayW;
  const holidaySet = new Set(Array.isArray(data?.plan?.holidays) ? data.plan.holidays : []);
  const today     = todayISO();
  function dayOff(iso){ return daysBetween(startD,iso); }

  const days = Array.from({length:totalDays},(_,i)=>{
    const d=addDays(parseISO(startD),i); const iso=fmtISO(d); const wd=d.getUTCDay();
    return { iso,wd,num:d.getUTCDate(),month:d.getUTCMonth(),year:d.getUTCFullYear(),isWeekend:wd===0||wd===6,isToday:iso===today };
  });

  const monthSpans=[]; let curKey=null,curStart=0;
  days.forEach((d,i)=>{
    const k=`${d.year}-${d.month}`;
    if(k!==curKey){ if(curKey!==null) monthSpans.push({label:`${MONTHS_IT[days[curStart].month]} ${days[curStart].year}`,startPx:curStart*dayW,widthPx:(i-curStart)*dayW}); curKey=k; curStart=i; }
  });
  monthSpans.push({label:`${MONTHS_IT[days[curStart].month]} ${days[curStart].year}`,startPx:curStart*dayW,widthPx:(days.length-curStart)*dayW});

  const laneMap=new Map();
  blocksFlat.forEach(b=>{
    const key=`${b.phaseId}:${b.lineId}`;
    if(!laneMap.has(key)) laneMap.set(key,{phaseId:Number(b.phaseId),phaseName:b.phaseName,lineNo:b.lineNo,lineId:b.lineId,lineLabel:b.lineLabel||"",availableFrom:b.availableFrom||null,availableTo:b.availableTo||null,customStopFrom:b.customStopFrom||null,customStopTo:b.customStopTo||null,speedMultiplier:b.speedMultiplier||null,lineIsLocked:!!b.lineIsLocked,blocks:[]});
    if(!filterEp || b.episodeNumber===filterEp) laneMap.get(key).blocks.push(b);
  });
  const lanes=[...laneMap.values()].sort((a,b)=>a.phaseId!==b.phaseId?a.phaseId-b.phaseId:a.lineNo-b.lineNo);
  const phaseFirstSet=new Set(); let lastPhId=null;
  lanes.forEach((l,i)=>{ if(l.phaseId!==lastPhId){phaseFirstSet.add(i);lastPhId=l.phaseId;} });
  const todayOff=dayOff(today);

  const fbtn = (label, active, onClick, col) => (
    <button onClick={onClick} style={{ padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:500,cursor:"pointer",transition:"all 0.15s",border:active?`1px solid ${col||"rgba(212,165,80,0.5)"}` :"1px solid rgba(255,255,255,0.10)",background:active?(col?col.replace("0.5","0.08"):"rgba(212,165,80,0.08)"):"transparent",color:active?(col||"#d4a550"):"rgba(232,230,223,0.45)" }}>{label}</button>
  );

  return (
    <div ref={ganttRef} style={{ display:"flex",flexDirection:"column",height:"100%",minHeight:400,background:"#0a0a0f",color:"#e8e6df",fontFamily:"'Inter',-apple-system,sans-serif",fontSize:13,overflow:"hidden",borderRadius:10,border:"1px solid rgba(255,255,255,0.07)" }}>
      {laneEdit && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",zIndex:200,display:"flex",alignItems:"center",justifyContent:"center" }} onClick={()=>setLaneEdit(null)}>
          <div style={{ background:"#12121a",border:"1px solid rgba(212,165,80,0.35)",borderRadius:12,padding:"24px 28px",minWidth:340,display:"flex",flexDirection:"column",gap:16 }} onClick={e=>e.stopPropagation()}>
            <div style={{ fontWeight:700,fontSize:15,color:"#d4a550" }}>{laneEdit.phaseName} — {laneEdit.label}</div>

            {/* Velocità */}
            <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
              <label style={{ fontSize:11,fontWeight:600,color:"rgba(232,230,223,0.55)",textTransform:"uppercase",letterSpacing:"0.07em" }}>Turni per episodio</label>
              <input type="number" min={1} max={99} value={laneEdit.turni}
                onChange={e=>setLaneEdit(l=>({...l,turni:Math.max(1,parseInt(e.target.value)||1)}))}
                style={{ background:"rgba(255,255,255,0.04)",border:"1px solid rgba(212,165,80,0.35)",borderRadius:6,color:"#d4a550",fontSize:18,fontWeight:700,padding:"8px 12px",width:80,textAlign:"center" }} />
            </div>

            {/* Data inizio */}
            <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
              <label style={{ fontSize:11,fontWeight:600,color:"rgba(232,230,223,0.55)",textTransform:"uppercase",letterSpacing:"0.07em" }}>Disponibile dal</label>
              <input type="date" value={laneEdit.availableFrom||''}
                onChange={e=>setLaneEdit(l=>({...l,availableFrom:e.target.value}))}
                style={{ background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:6,color:"#e8e6df",fontSize:13,padding:"7px 10px",colorScheme:"dark",outline:"none" }} />
              <label style={{ display:"flex",alignItems:"center",gap:6,fontSize:11,color:"rgba(232,230,223,0.55)",cursor:"pointer" }}>
                <input type="checkbox" checked={laneEdit.applyToAllSamePhase||false}
                  onChange={e=>setLaneEdit(l=>({...l,applyToAllSamePhase:e.target.checked}))}
                  style={{ accentColor:"#d4a550" }} />
                Applica a tutte le linee di {laneEdit.phaseName}
              </label>
            </div>

            {/* Data fine */}
            <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
              <label style={{ fontSize:11,fontWeight:600,color:"rgba(232,230,223,0.55)",textTransform:"uppercase",letterSpacing:"0.07em" }}>Disponibile fino al</label>
              <input type="date" value={laneEdit.availableTo||''}
                onChange={e=>setLaneEdit(l=>({...l,availableTo:e.target.value}))}
                style={{ background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.12)",borderRadius:6,color:"#e8e6df",fontSize:13,padding:"7px 10px",colorScheme:"dark",outline:"none" }} />
            </div>



            <div style={{ display:"flex",gap:10,justifyContent:"flex-end" }}>
              <button onClick={()=>setLaneEdit(null)} style={{ background:"none",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,color:"rgba(232,230,223,0.55)",cursor:"pointer",padding:"7px 16px",fontSize:13 }}>Annulla</button>
              <button onClick={async()=>{
                try {
                  await fetchJson(`/postplans/${planId}/lane-speed`, { method:"POST", body:JSON.stringify({
                    lineId: laneEdit.lineId,
                    turni: laneEdit.turni,
                    availableFrom: laneEdit.availableFrom || null,
                    availableTo: laneEdit.availableTo || null,
                    applyToAllSamePhase: laneEdit.applyToAllSamePhase || false,

                  })});
                  setLaneEdit(null);
                  refreshPlan();
                } catch(e) { alert(e.message); }
              }} style={{ background:"rgba(212,165,80,0.15)",border:"1px solid rgba(212,165,80,0.4)",borderRadius:6,color:"#d4a550",cursor:"pointer",padding:"7px 16px",fontSize:13,fontWeight:700 }}>Applica</button>
            </div>
          </div>
        </div>
      )}
      <BlockTooltip block={tooltip} lanes={lanes.filter(l=>!l.isJolly||true)} onClose={()=>setTooltip(null)} workingDays={data?.plan?.working_days ?? [1,2,3,4,5]} onRefresh={()=>{ refreshPlan(); }}/>

      {/* Top bar */}
      <div style={{ padding:"12px 16px",borderBottom:"1px solid rgba(255,255,255,0.06)",display:"flex",alignItems:"center",justifyContent:"space-between",flexShrink:0,gap:12,flexWrap:"wrap" }}>
        <div>
          <div style={{ display:"flex",alignItems:"center",gap:6,marginBottom:4 }}>
            <button onClick={()=>nav("/productions")} style={{ background:"none",border:"none",color:"rgba(212,165,80,0.6)",cursor:"pointer",fontSize:11,padding:0,fontFamily:"inherit",textDecoration:"underline",textUnderlineOffset:2 }}>Produzioni</button>
            <span style={{ color:"rgba(232,230,223,0.20)",fontSize:11 }}>›</span>
            {plan.productionId && <button onClick={()=>nav(`/productions/${plan.productionId}`)} style={{ background:"none",border:"none",color:"rgba(212,165,80,0.6)",cursor:"pointer",fontSize:11,padding:0,fontFamily:"inherit",textDecoration:"underline",textUnderlineOffset:2 }}>{plan.productionName || "Produzione"}</button>}
            {plan.productionId && <span style={{ color:"rgba(232,230,223,0.20)",fontSize:11 }}>›</span>}
            <span style={{ color:"rgba(232,230,223,0.45)",fontSize:11 }}>{plan.name}</span>
          </div>
          <div style={{ fontSize:15,fontWeight:600 }}>{plan.name}</div>
          <div style={{ fontSize:11,color:"rgba(232,230,223,0.40)",marginTop:2 }}>Piano di post · {episodes.length} episodi · {blocksFlat.length} blocchi</div>
        </div>
        <div style={{ display:"flex",gap:6,flexWrap:"wrap",alignItems:"center" }}>
          {fbtn("Tutti", filterEp===null, ()=>setFilterEp(null))}
          {episodes.map(ep=>{ const col=EP_COLORS[(ep.episodeNo-1)%EP_COLORS.length]; return <button key={ep.id} onClick={()=>setFilterEp(filterEp===ep.episodeNo?null:ep.episodeNo)} style={{ padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:500,cursor:"pointer",transition:"all 0.15s",border:filterEp===ep.episodeNo?`1px solid ${col.border}`:"1px solid rgba(255,255,255,0.10)",background:filterEp===ep.episodeNo?col.bg:"transparent",color:filterEp===ep.episodeNo?col.text:"rgba(232,230,223,0.45)" }}>EP.{ep.episodeNo}</button>; })}
        </div>
      </div>

      {/* Barra strumenti */}
      <div style={{ padding:"6px 16px",borderBottom:"1px solid rgba(255,255,255,0.06)",display:"flex",alignItems:"center",gap:6,flexShrink:0,flexWrap:"wrap" }}>
        <button onClick={()=>setShowSquad(v=>!v)} style={{ padding:"4px 12px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${showSquad?"rgba(212,165,80,0.5)":"rgba(255,255,255,0.10)"}`,background:showSquad?"rgba(212,165,80,0.08)":"transparent",color:showSquad?"#d4a550":"rgba(232,230,223,0.45)" }}>◈ Squadra</button>
        <div style={{ width:1,height:16,background:"rgba(255,255,255,0.08)",margin:"0 4px" }}/>
        <button onClick={()=>setShowEpisodeAssign(true)} style={{ background:"none",border:"1px solid rgba(212,165,80,0.2)",borderRadius:6,color:"rgba(212,165,80,0.6)",fontSize:11,cursor:"pointer",padding:"4px 10px",fontWeight:600 }}>🎬 Episodi</button>
        <button onClick={()=>setShowParallelismi(true)} style={{ background:"none",border:"1px solid rgba(212,165,80,0.2)",borderRadius:6,color:"rgba(212,165,80,0.6)",fontSize:11,cursor:"pointer",padding:"4px 10px",fontWeight:600 }}>⚙ Parallelismi</button>
        <button onClick={()=>setShowStopLinee(true)} style={{ background:"none",border:"1px solid rgba(248,113,113,0.2)",borderRadius:6,color:"rgba(248,113,113,0.6)",fontSize:11,cursor:"pointer",padding:"4px 10px",fontWeight:600 }}>⏸ Stop linee</button>
        <button onClick={()=>setShowDatePanel(v=>!v)} style={{ background:showDatePanel?"rgba(212,165,80,0.08)":"none",border:`1px solid ${showDatePanel?"rgba(212,165,80,0.5)":"rgba(212,165,80,0.2)"}`,borderRadius:6,color:showDatePanel?"#d4a550":"rgba(212,165,80,0.6)",fontSize:11,cursor:"pointer",padding:"4px 10px",fontWeight:600 }}>📅 Date & Margini</button>
        <button onClick={()=>setShowAddLineNew(true)} style={{ background:"none",border:"1px solid rgba(212,165,80,0.2)",borderRadius:6,color:"rgba(212,165,80,0.6)",fontSize:11,cursor:"pointer",padding:"4px 10px",fontWeight:600 }}>+ Linea</button>
      </div>

      {/* Drawer squadra */}
      {showSquad && (
        <div style={{ position:"fixed",top:0,right:0,width:360,bottom:0,zIndex:300,borderLeft:"1px solid rgba(255,255,255,0.07)",boxShadow:"-8px 0 24px rgba(0,0,0,0.4)" }}>
          <SquadPanel planId={planId} productionId={data?.plan?.productionId} planStart={data?.episodes?.[0]?.material_arrival_date || data?.plan?.tx_start_date} planEnd={data?.episodes?.[data?.episodes?.length-1]?.tx_date} onClose={()=>{ setShowSquad(false); refreshPlan(); }} />
        </div>
      )}
      {/* Chip metriche */}
      <div style={{ display:"flex",gap:6,padding:"6px 16px",borderBottom:"1px solid rgba(255,255,255,0.06)",flexWrap:"wrap" }}>
        {episodes.map(ep=>{ const risk=ep.metrics?.risk||"green"; return (
          <div key={ep.id} title={`EP.${ep.episodeNo} · Consegna: ${ep.metrics?.deliveryDate||"—"} · Margine: ${ep.metrics?.marginWorkingDays??"?"} gg`}
            style={{ padding:"3px 10px",borderRadius:20,fontSize:11,fontWeight:500,border:`1px solid ${RISK_BORDER[risk]}`,background:RISK_BG[risk],color:RISK_COLOR[risk],cursor:"default" }}>
            EP.{ep.episodeNo} · {ep.metrics?.marginWorkingDays??"?"}gg
          </div>
        );})}
          <div style={{ marginLeft:"auto",display:"flex",alignItems:"center",gap:10 }}>
          <div style={{ display:"flex",gap:4 }}>
            <button onClick={()=>exportPDF('poster')} disabled={exporting} title="Un unico file largo — ideale per condivisione digitale" style={{ background:"none",border:"1px solid rgba(255,255,255,0.08)",borderRadius:4,color:exporting?"rgba(232,230,223,0.2)":"rgba(232,230,223,0.4)",cursor:exporting?"default":"pointer",padding:"2px 8px",fontSize:11,lineHeight:1,display:"flex",alignItems:"center",gap:4 }}>
              {exporting ? "⏳" : "⬇"} Poster
            </button>
            <button onClick={()=>exportPDF('print')} disabled={exporting} title="Fogli A4 affiancati da stampare e scotchare" style={{ background:"none",border:"1px solid rgba(255,255,255,0.08)",borderRadius:4,color:exporting?"rgba(232,230,223,0.2)":"rgba(232,230,223,0.4)",cursor:exporting?"default":"pointer",padding:"2px 8px",fontSize:11,lineHeight:1,display:"flex",alignItems:"center",gap:4 }}>
              {exporting ? "⏳" : "🖨"} Stampa
            </button>
          </div>
          <div style={{ display:"flex",alignItems:"center",gap:4 }}>
            <span style={{ fontSize:9,color:"rgba(232,230,223,0.25)",letterSpacing:"0.08em",textTransform:"uppercase",marginRight:2 }}>Zoom</span>
            <button onClick={()=>setDayW(w=>Math.max(14,w-4))} style={{ background:"none",border:"1px solid rgba(255,255,255,0.08)",borderRadius:4,color:"rgba(232,230,223,0.4)",cursor:"pointer",padding:"1px 7px",fontSize:13,lineHeight:1 }}>−</button>
            <span style={{ fontSize:10,color:"rgba(232,230,223,0.3)",minWidth:20,textAlign:"center" }}>{dayW}</span>
            <button onClick={()=>setDayW(w=>Math.min(60,w+4))} style={{ background:"none",border:"1px solid rgba(255,255,255,0.08)",borderRadius:4,color:"rgba(232,230,223,0.4)",cursor:"pointer",padding:"1px 7px",fontSize:13,lineHeight:1 }}>+</button>
          </div>
          <div style={{ fontSize:11,color:"rgba(232,230,223,0.28)" }}>Oggi: {today.split("-").reverse().join("-")}</div>
        </div>
      </div>

      {/* Corpo */}
      <div style={{ display:"flex",flex:1,overflow:"hidden",position:"relative" }}>
        {/* Pannello Cronologia Snapshots */}
        {showSnapshots && (
          <div style={{ position:"absolute",top:0,left:0,width:LABEL_W,height:"100%",background:"#0f0f16",borderRight:"1px solid rgba(212,165,80,0.2)",zIndex:20,display:"flex",flexDirection:"column",boxShadow:"4px 0 20px rgba(0,0,0,0.5)" }}>
            <div style={{ padding:"12px 14px",borderBottom:"1px solid rgba(255,255,255,0.08)",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
              <span style={{ fontSize:12,fontWeight:700,color:"#d4a550",letterSpacing:"0.04em" }}>STORICO MODIFICHE</span>
              <button onClick={()=>setShowSnapshots(false)} style={{ background:"none",border:"none",color:"rgba(232,230,223,0.4)",cursor:"pointer",fontSize:16,padding:0 }}>×</button>
            </div>
            <div style={{ padding:"8px 14px",borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
              <button onClick={createSnapshot} disabled={snapBusy}
                style={{ width:"100%",padding:"6px 0",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",
                  border:"1px solid rgba(212,165,80,0.4)",background:"rgba(212,165,80,0.08)",color:"#d4a550" }}>
                {snapBusy ? "..." : "+ Salva stato attuale"}
              </button>
            </div>
            <div style={{ flex:1,overflowY:"auto",scrollbarWidth:"thin",scrollbarColor:"rgba(212,165,80,0.3) transparent" }}>
              {snapshots.length === 0 ? (
                <div style={{ padding:"16px 14px",fontSize:11,color:"rgba(232,230,223,0.3)",textAlign:"center" }}>
                  Nessuno snapshot salvato.<br/>Gli snapshot vengono creati automaticamente ad ogni modifica.
                </div>
              ) : snapshots.map(s => (
                <div key={s.id} style={{ padding:"10px 14px",borderBottom:"1px solid rgba(255,255,255,0.12)",display:"flex",flexDirection:"column",gap:4 }}>
                  <div style={{ fontSize:10,color:"rgba(232,230,223,0.35)" }}>
                    {new Date(s.created_at).toLocaleString('it-IT',{day:'2-digit',month:'2-digit',hour:'2-digit',minute:'2-digit'})}
                  </div>
                  <div style={{ fontSize:11,color:"rgba(232,230,223,0.7)",lineHeight:1.3 }}>
                    {s.label || `Snapshot #${s.id}`}
                  </div>
                  <div style={{ fontSize:10,color:"rgba(212,165,80,0.5)" }}>{s.blocks_count} blocchi</div>
                  <button onClick={()=>restoreSnapshot(s.id)} disabled={snapBusy}
                    style={{ marginTop:2,padding:"4px 8px",borderRadius:5,fontSize:10,fontWeight:600,cursor:"pointer",
                      border:"1px solid rgba(212,165,80,0.3)",background:"transparent",color:"rgba(212,165,80,0.7)" }}>
                    ↩ Ripristina
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        {/* Labels */}
        <div style={{ width:LABEL_W,flexShrink:0,borderRight:"1px solid rgba(255,255,255,0.06)",display:"flex",flexDirection:"column" }}>
          <div style={{ height:HEADER_H,flexShrink:0,borderBottom:"1px solid rgba(255,255,255,0.10)",background:"#12121a",display:"flex",alignItems:"center",justifyContent:"center",padding:"0 8px" }}>
            <button onClick={() => { setShowSnapshots(v => !v); if (!showSnapshots) loadSnapshots(); }} style={{ width:"100%",padding:"5px 8px",borderRadius:6,fontSize:11,fontWeight:600,cursor:"pointer",border:`1px solid ${showSnapshots?"rgba(212,165,80,0.5)":"rgba(255,255,255,0.10)"}`,background:showSnapshots?"rgba(212,165,80,0.08)":"transparent",color:showSnapshots?"#d4a550":"rgba(232,230,223,0.45)",display:"flex",alignItems:"center",gap:5,justifyContent:"center" }}>↩ Storico modifiche</button>
          </div>
          <div ref={labelsRef} style={{ overflow:"hidden",flex:1 }}>
            {lanes.map((lane,i)=>(
              <div key={`${lane.phaseId}:${lane.lineId}`} style={{ height:ROW_H,display:"flex",flexDirection:"row",alignItems:"center",padding:"0 6px 0 14px",borderBottom:"1px solid rgba(255,255,255,0.12)",borderTop:phaseFirstSet.has(i)?"1px solid rgba(255,255,255,0.10)":undefined,background:lane.lineIsLocked?"rgba(212,165,80,0.04)":"#12121a",overflow:"hidden",gap:4,transition:"background 0.15s" }}>
                <div onClick={()=>setLaneEdit({lineId:lane.lineId,lineNo:lane.lineNo,phaseId:lane.phaseId,phaseName:lane.phaseName,label:lane.lineLabel||`L${lane.lineNo}`,turni:lane.speedMultiplier||1,availableFrom:lane.availableFrom||'',availableTo:lane.availableTo||'',customStopFrom:lane.customStopFrom||'',customStopTo:lane.customStopTo||''})} style={{ flex:1,display:"flex",flexDirection:"column",justifyContent:"center",overflow:"hidden",gap:1,cursor:"pointer" }} onMouseEnter={e=>e.currentTarget.parentElement.style.background=lane.lineIsLocked?"rgba(212,165,80,0.07)":"#1a1d23"} onMouseLeave={e=>e.currentTarget.parentElement.style.background=lane.lineIsLocked?"rgba(212,165,80,0.04)":"#12121a"}>
                  <span style={{ fontSize:9,fontWeight:700,letterSpacing:"0.05em",textTransform:"uppercase",color:"rgba(212,165,80,0.6)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",width:"100%" }}>{lane.phaseName}</span>
                  <span style={{ fontSize:11,color:lane.lineIsLocked?"rgba(212,165,80,0.8)":"rgba(232,230,223,0.55)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap",width:"100%" }}>{lane.lineLabel||(`L${lane.lineNo}`)}</span>
                </div>
                <button
                  title={lane.lineIsLocked ? "Linea bloccata — clicca per sbloccare" : "Clicca per bloccare la linea"}
                  onClick={async()=>{
                    try {
                      const r = await fetchJson(`/postplans/${planId}/lines/${lane.lineId}/lock`, {method:"PATCH"});
                      if(r.ok) refreshPlan();
                    } catch(e) { console.error("lock error",e); }
                  }}
                  style={{ flexShrink:0,background:"none",border:"none",cursor:"pointer",padding:"2px 3px",fontSize:13,opacity:lane.lineIsLocked?1:0.25,color:lane.lineIsLocked?"#d4a550":"rgba(232,230,223,0.6)",transition:"opacity 0.15s" }}
                  onMouseEnter={e=>e.currentTarget.style.opacity="1"}
                  onMouseLeave={e=>e.currentTarget.style.opacity=lane.lineIsLocked?"1":"0.25"}
                >🔒</button>
              </div>
            ))}
          </div>
        </div>

        {/* Griglia */}
        <div ref={gridRef} onScroll={syncScroll} style={{ flex:1,overflow:"auto",scrollbarWidth:"thin",scrollbarColor:"rgba(212,165,80,0.35) rgba(255,255,255,0.04)" }}>
          <div style={{ width:totalW,minWidth:totalW }}>
            {/* Header */}
            <div style={{ position:"sticky",top:0,zIndex:10,background:"#12121a",borderBottom:"1px solid rgba(255,255,255,0.10)",height:HEADER_H }}>
              <div style={{ height:20,position:"relative",borderBottom:"1px solid rgba(255,255,255,0.06)" }}>
                {monthSpans.map(m=>(
                  <span key={m.label} style={{ position:"absolute",left:m.startPx,width:m.widthPx,display:"flex",alignItems:"center",height:20,padding:"0 8px",fontSize:10,fontWeight:700,letterSpacing:"0.06em",textTransform:"uppercase",color:"#d4a550",overflow:"hidden",whiteSpace:"nowrap" }}>{m.label}</span>
                ))}
              </div>
              <div style={{ height:HEADER_H-20,display:"flex" }}>
                {days.map(d=>(
                  <div key={d.iso} style={{ width:dayW,flexShrink:0,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:1,borderRight:"1px solid rgba(255,255,255,0.12)",background:d.isToday?"rgba(212,165,80,0.10)":d.isWeekend?"rgba(255,255,255,0.045)":"transparent",position:"relative" }}>
                    {holidaySet.has(d.iso)&&!d.isWeekend&&<div style={{ position:"absolute",inset:0,background:"repeating-linear-gradient(-45deg,rgba(239,68,68,0.18) 0px,rgba(239,68,68,0.18) 2px,transparent 2px,transparent 6px)",pointerEvents:"none" }}/>}
                    <span style={{ fontSize:10,color:d.isToday?"#d4a550":d.isWeekend?"#4a4a5a":"#b8b4ac",fontWeight:d.isToday?700:500,position:"relative",zIndex:1 }}>{d.num}</span>
                    <span style={{ fontSize:9,color:d.isWeekend?"#3a3a4a":"#7a7870",textTransform:"uppercase",position:"relative",zIndex:1 }}>{DAYS_IT[d.wd]}</span>
                    {d.isToday&&<div style={{ position:"absolute",bottom:0,left:0,right:0,height:2,background:"#d4a550" }}/>}
                  </div>
                ))}
              </div>
            </div>

            {/* Righe */}
            <div style={{ position:"relative" }}>
              {lanes.map((lane,ri)=>(
                <div key={`${lane.phaseId}:${lane.lineId}`} style={{ height:ROW_H,display:"flex",position:"relative",borderBottom:"1px solid rgba(255,255,255,0.14)",borderTop:phaseFirstSet.has(ri)?"1px solid rgba(255,255,255,0.20)":undefined }}>
                  {days.map(d=>(
                    <div key={d.iso} style={{ width:dayW,flexShrink:0,borderRight:"1px solid rgba(255,255,255,0.15)",background:d.isToday?"rgba(212,165,80,0.04)":d.isWeekend?"rgba(255,255,255,0.045)":"transparent",position:"relative",overflow:"hidden" }}>
                      {holidaySet.has(d.iso)&&!d.isWeekend&&<div style={{ position:"absolute",inset:0,background:"repeating-linear-gradient(-45deg,rgba(239,68,68,0.12) 0px,rgba(239,68,68,0.12) 2px,transparent 2px,transparent 6px)",pointerEvents:"none" }}/>}

                    </div>
                  ))}
                  {/* Overlay stop a livello lane: copre l'intero intervallo, anche dove non ci sono blocchi */}
                  {lane.customStopFrom&&lane.customStopTo&&(()=>{
                    const sf=lane.customStopFrom,st=lane.customStopTo;
                    const startOff=dayOff(sf)*dayW;
                    const endOff=(dayOff(st)+1)*dayW;
                    if(endOff<=0||startOff>=totalDays*dayW) return null;
                    return <div key="lane-stop" style={{position:"absolute",left:Math.max(startOff,0),width:Math.min(endOff,totalDays*dayW)-Math.max(startOff,0),top:0,bottom:0,background:"repeating-linear-gradient(-45deg,rgba(239,68,68,0.5) 0px,rgba(239,68,68,0.5) 3px,transparent 3px,transparent 7px)",pointerEvents:"none",zIndex:1}}/>;
                  })()}
                  {lane.blocks.map(b=>{ const col=EP_COLORS[b.episodeNumber ? (b.episodeNumber-1)%EP_COLORS.length : 0]; const left=dayOff(b.startDate)*dayW; const width=Math.max((daysBetween(b.startDate,b.endDate)+1)*dayW-2,dayW-2);
                    return (
                      <div key={b.id} onClick={()=>setTooltip({...b, _allLanes: lanes})} title={`${b.episodeNumber ? "EP."+b.episodeNumber+" · " : ""}${b.phaseName} · ${b.startDate}→${b.endDate} (${b.durWork}gg)`}
                        style={{ position:"absolute",left,width,top:4,bottom:4,borderRadius:4,background:col.bg,border:`1px solid ${b.isLocked?col.text:col.border}`,color:col.text,display:"flex",alignItems:"center",padding:"0 5px",fontSize:10,fontWeight:600,cursor:"pointer",overflow:"hidden",whiteSpace:"nowrap",zIndex:2,boxShadow:b.isLocked?`0 0 0 1px ${col.border}`:undefined }}
                        onMouseEnter={e=>e.currentTarget.style.filter="brightness(1.25)"}
                        onMouseLeave={e=>e.currentTarget.style.filter=""}>
                        {lane.customStopFrom&&lane.customStopTo&&(()=>{
                          const sf=lane.customStopFrom,st=lane.customStopTo;
                          if(b.endDate<sf||b.startDate>st) return null;
                          const cs=b.startDate>sf?b.startDate:sf;
                          const ce=b.endDate<st?b.endDate:st;
                          // Overlay è figlio del blocco (position:absolute) → left/width sono relativi al blocco, non alla timeline
                          const blockOff=dayOff(b.startDate);
                          const startOff=(dayOff(cs)-blockOff)*dayW;
                          const endOff=(dayOff(ce)+1-blockOff)*dayW;
                          return <div key="stop" style={{position:"absolute",left:startOff,width:endOff-startOff,top:0,bottom:0,background:"repeating-linear-gradient(-45deg,rgba(239,68,68,0.7) 0px,rgba(239,68,68,0.7) 3px,transparent 3px,transparent 7px)",pointerEvents:"none",zIndex:3}}/>;
                        })()}
                        {weekendOffsetsInBlock(b.startDate,b.endDate,!!b.allowSat,!!b.allowSun,dayW).map(offPx=>(
                          <div key={offPx} style={{ position:"absolute",left:offPx,top:0,width:dayW,bottom:0,background:"rgba(10,10,16,0.82)",pointerEvents:"none",zIndex:3 }}/>
                        ))}
                        <span style={{ overflow:"hidden",textOverflow:"ellipsis",position:"relative",zIndex:4,flex:1 }}>{b.episodeNumber?"EP."+b.episodeNumber:(b.partLabel||b.lineLabel||"Supporto")}{width>60&&b.episodeNumber?(b.partLabel?` — ${b.partLabel}`:b.lineLabel?` — ${b.lineLabel}`:""):""}</span>
                        <span onClick={e=>{ e.stopPropagation(); fetchJson(`/postplans/blocks/${b.id}/lock`,{method:"PATCH"}).then(()=>{ refreshPlan(); }); }} title={b.isLocked?"Sblocca":"Blocca"} style={{ position:"absolute",top:2,right:2,zIndex:5,fontSize:9,cursor:"pointer",background:"rgba(0,0,0,0.3)",borderRadius:3,padding:"1px 3px",opacity:b.isLocked?1:0.30 }}>{b.isLocked?"🔒":"🔓"}</span>
                      </div>
                    );
                  })}
                </div>
              ))}
              {todayOff>=0&&todayOff<totalDays&&<div style={{ position:"absolute",top:0,bottom:0,left:todayOff*dayW+1,width:2,background:"#d4a550",opacity:0.55,zIndex:8,pointerEvents:"none" }}/>}
              {episodes.map(ep=>{ const off=dayOff(ep.txDate); if(off<0||off>=totalDays) return null; const col=EP_COLORS[(ep.episodeNo-1)%EP_COLORS.length];
                return <div key={`tx-${ep.id}`} title={`TX EP.${ep.episodeNo} — ${ep.txDate}`} style={{ position:"absolute",top:0,bottom:0,left:off*dayW,width:2,background:col.border,zIndex:6,pointerEvents:"none" }}><span style={{ position:"absolute",top:4,left:4,fontSize:9,fontWeight:700,color:col.text,whiteSpace:"nowrap",textTransform:"uppercase",letterSpacing:"0.04em" }}>TX{ep.episodeNo}</span></div>;
              })}
              {episodes.map(ep=>{ if(!ep.metrics?.deliveryDate) return null; const off=dayOff(ep.metrics.deliveryDate); if(off<0||off>=totalDays) return null;
                return <div key={`del-${ep.id}`} title={`Consegna EP.${ep.episodeNo} — ${ep.metrics.deliveryDate}`} style={{ position:"absolute",top:0,bottom:0,left:off*dayW,width:1,borderLeft:"1px dashed rgba(255,255,255,0.22)",zIndex:5,pointerEvents:"none" }}/>;
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Legenda */}
      <div style={{ display:"flex",alignItems:"center",gap:14,padding:"6px 16px",borderTop:"1px solid rgba(255,255,255,0.06)",flexWrap:"wrap" }}>
        {episodes.map(ep=>{ const col=EP_COLORS[(ep.episodeNo-1)%EP_COLORS.length]; return <div key={ep.id} style={{ display:"flex",alignItems:"center",gap:5,fontSize:11,color:"rgba(232,230,223,0.45)" }}><div style={{ width:10,height:10,borderRadius:2,background:col.bg,border:`1px solid ${col.border}` }}/>EP.{ep.episodeNo}</div>; })}
        <span style={{ fontSize:11,color:"rgba(232,230,223,0.20)",margin:"0 4px" }}>|</span>
        <div style={{ display:"flex",alignItems:"center",gap:5,fontSize:11,color:"rgba(232,230,223,0.35)" }}><div style={{ width:2,height:10,background:"rgba(255,255,255,0.4)" }}/>TX date</div>
        <div style={{ display:"flex",alignItems:"center",gap:5,fontSize:11,color:"rgba(232,230,223,0.35)" }}><div style={{ width:1,height:10,borderLeft:"1px dashed rgba(255,255,255,0.3)" }}/>Consegna</div>
        <div style={{ display:"flex",alignItems:"center",gap:5,fontSize:11,color:"rgba(232,230,223,0.35)" }}><div style={{ width:2,height:10,background:"#d4a550",opacity:0.6 }}/>Oggi</div>
        <div style={{ marginLeft:"auto",fontSize:10,color:"rgba(232,230,223,0.22)" }}>{lanes.length} righe · {totalDays} giorni</div>
      </div>
    {/* Dialog aggiungi linea temporanea */}
    {showEpisodeAssign && (
      <EpisodeAssignModal
        planId={planId}
        episodes={episodes}
        blocksFlat={blocksFlat}
        onClose={()=>setShowEpisodeAssign(false)}
        onSaved={()=>{ setShowEpisodeAssign(false); refreshPlan(); }}
      />
    )}
    {showStopLinee && (
      <StopLineePanel
        planId={planId}
        lanes={lanes}
        onClose={()=>setShowStopLinee(false)}
        onSaved={()=>{ setShowStopLinee(false); refreshPlan(); }}
      />
    )}
    {showDatePanel && (
      <div style={{ position:"fixed",top:0,right:0,width:420,bottom:0,zIndex:300,background:"#0f0f16",borderLeft:"1px solid rgba(212,165,80,0.2)",boxShadow:"-8px 0 24px rgba(0,0,0,0.4)",display:"flex",flexDirection:"column" }}>
        <div style={{ padding:"16px 20px",borderBottom:"1px solid rgba(255,255,255,0.08)",display:"flex",alignItems:"center",justifyContent:"space-between" }}>
          <span style={{ fontSize:13,fontWeight:700,color:"#d4a550",letterSpacing:"0.04em" }}>DATE & MARGINI</span>
          <button onClick={()=>setShowDatePanel(false)} style={{ background:"none",border:"none",color:"rgba(232,230,223,0.4)",cursor:"pointer",fontSize:18,padding:0 }}>×</button>
        </div>
        <div style={{ overflowY:"auto",flex:1,scrollbarWidth:"thin",scrollbarColor:"rgba(212,165,80,0.3) transparent" }}>
          <table style={{ width:"100%",borderCollapse:"collapse",fontSize:11 }}>
            <thead>
              <tr style={{ background:"rgba(255,255,255,0.03)" }}>
                <th style={{ padding:"8px 12px",textAlign:"left",color:"rgba(232,230,223,0.4)",fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:"1px solid rgba(255,255,255,0.06)" }}>Ep</th>
                <th style={{ padding:"8px 12px",textAlign:"left",color:"rgba(232,230,223,0.4)",fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:"1px solid rgba(255,255,255,0.06)" }}>Arrivo RX</th>
                <th style={{ padding:"8px 12px",textAlign:"left",color:"rgba(232,230,223,0.4)",fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:"1px solid rgba(255,255,255,0.06)" }}>Consegna</th>
                <th style={{ padding:"8px 12px",textAlign:"left",color:"rgba(232,230,223,0.4)",fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:"1px solid rgba(255,255,255,0.06)" }}>TX</th>
                <th style={{ padding:"8px 12px",textAlign:"center",color:"rgba(232,230,223,0.4)",fontWeight:600,fontSize:10,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:"1px solid rgba(255,255,255,0.06)" }}>Margine</th>
              </tr>
            </thead>
            <tbody>
              {episodes.map(ep => {
                const m = ep.metrics;
                const risk = m?.risk || "unknown";
                const margin = m?.marginWorkingDays ?? null;
                const delivery = m?.deliveryDate || null;
                const RISK_C = { green:"#4ade80", yellow:"#fbbf24", orange:"#fb923c", red:"#f87171", unknown:"rgba(232,230,223,0.3)" };
                const RISK_B = { green:"rgba(74,222,128,0.12)", yellow:"rgba(251,191,36,0.12)", orange:"rgba(251,146,60,0.12)", red:"rgba(248,113,113,0.12)", unknown:"transparent" };
                const col = EP_COLORS[(ep.episodeNo-1) % EP_COLORS.length];
                const fmtDate = d => d ? d.slice(5).split("-").reverse().join("/") : "—";
                return (
                  <tr key={ep.id} style={{ borderBottom:"1px solid rgba(255,255,255,0.04)" }}>
                    <td style={{ padding:"7px 12px" }}>
                      <div style={{ display:"flex",alignItems:"center",gap:5 }}>
                        <div style={{ width:8,height:8,borderRadius:2,background:col.bg,border:`1px solid ${col.border}`,flexShrink:0 }}/>
                        <span style={{ color:col.text,fontWeight:600 }}>EP.{ep.episodeNo}</span>
                      </div>
                    </td>
                    <td style={{ padding:"7px 12px",color:"rgba(232,230,223,0.55)" }}>{fmtDate(ep.arrivalDate)}</td>
                    <td style={{ padding:"7px 12px",color:"rgba(232,230,223,0.55)" }}>{fmtDate(delivery)}</td>
                    <td style={{ padding:"7px 12px",color:"rgba(232,230,223,0.55)" }}>{fmtDate(ep.txDate)}</td>
                    <td style={{ padding:"7px 12px",textAlign:"center" }}>
                      {margin !== null ? (
                        <span style={{ display:"inline-block",padding:"2px 8px",borderRadius:10,fontSize:10,fontWeight:700,background:RISK_B[risk],color:RISK_C[risk],border:`1px solid ${RISK_C[risk]}40` }}>
                          {margin}gg
                        </span>
                      ) : <span style={{ color:"rgba(232,230,223,0.2)" }}>—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    )}
    {showParallelismi && (
      <ParallelismiPanel
        planId={planId}
        episodes={episodes}
        lanes={lanes}
        onClose={()=>setShowParallelismi(false)}
        onSaved={()=>{ setShowParallelismi(false); refreshPlan(); }}
      />
    )}
    {showAddLineNew && (
      <AddLineModal
        planId={planId}
        productionId={data?.plan?.productionId}
        planStart={data?.episodes?.[0]?.material_arrival_date || data?.plan?.tx_start_date}
        planEnd={data?.episodes?.[data?.episodes?.length-1]?.tx_date}
        lanes={lanes}
        episodes={data?.episodes || []}
        onClose={()=>setShowAddLineNew(false)}
        onSaved={()=>{ setShowAddLineNew(false); refreshPlan(); }}
      />
    )}
    {showAddLine && (
      <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.7)",zIndex:100,display:"flex",alignItems:"center",justifyContent:"center" }}>
        <div style={{ background:"#111118",border:"1px solid rgba(212,165,80,0.2)",borderRadius:12,padding:28,width:420,display:"flex",flexDirection:"column",gap:16 }}>
          <div style={{ fontSize:16,fontWeight:800,color:"#e8e6df" }}>+ Aggiungi linea temporanea</div>
          <div style={{ display:"flex",flexDirection:"column",gap:4 }}>
            <label style={{ fontSize:11,color:"rgba(232,230,223,0.45)",textTransform:"uppercase",letterSpacing:"0.06em" }}>Fase</label>
            <select value={addLineForm.phaseId} onChange={e=>setAddLineForm(f=>({...f,phaseId:e.target.value}))}
              style={{ background:"#1a1a24",border:"1px solid rgba(255,255,255,0.15)",borderRadius:6,color:"#e8e6df",padding:"7px 10px",fontSize:13,appearance:"auto" }}>
              <option value="" style={{ background:"#1a1a24" }}>— Seleziona fase —</option>
              {[...new Map(lanes.map(l=>[l.phaseId,l.phaseName])).entries()].map(([id,name])=>(
                <option key={id} value={id} style={{ background:"#1a1a24",color:"#e8e6df" }}>{name}</option>
              ))}
            </select>
          </div>
          <div style={{ display:"flex",flexDirection:"column",gap:4 }}>
            <label style={{ fontSize:11,color:"rgba(232,230,223,0.45)",textTransform:"uppercase",letterSpacing:"0.06em" }}>Etichetta (opzionale)</label>
            <input value={addLineForm.label} onChange={e=>setAddLineForm(f=>({...f,label:e.target.value}))} placeholder="Es. Nome risorsa"
              style={{ background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,color:"#e8e6df",padding:"7px 10px",fontSize:13 }} />
          </div>
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
            <div style={{ display:"flex",flexDirection:"column",gap:4 }}>
              <label style={{ fontSize:11,color:"rgba(232,230,223,0.45)",textTransform:"uppercase",letterSpacing:"0.06em" }}>Disponibile dal *</label>
              <input type="date" value={addLineForm.startDate} onChange={e=>setAddLineForm(f=>({...f,startDate:e.target.value}))}
                style={{ background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,color:"#e8e6df",padding:"7px 10px",fontSize:13,colorScheme:"dark" }} />
            </div>
            <div style={{ display:"flex",flexDirection:"column",gap:4 }}>
              <label style={{ fontSize:11,color:"rgba(232,230,223,0.45)",textTransform:"uppercase",letterSpacing:"0.06em" }}>Disponibile fino al</label>
              <input type="date" value={addLineForm.endDate} onChange={e=>setAddLineForm(f=>({...f,endDate:e.target.value}))}
                style={{ background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,color:"#e8e6df",padding:"7px 10px",fontSize:13,colorScheme:"dark" }} />
            </div>
          </div>
          <div style={{ fontSize:11,color:"rgba(232,230,223,0.3)",fontStyle:"italic" }}>Se non specifichi la data fine, la linea rimane disponibile fino alla fine del piano.</div>
          <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:4 }}>
            <button onClick={()=>{ setShowAddLine(false); setAddLineForm({ phaseId:'', label:'', startDate:'', endDate:'' }); }} style={{ background:"none",border:"1px solid rgba(255,255,255,0.1)",borderRadius:6,color:"rgba(232,230,223,0.5)",padding:"8px 18px",cursor:"pointer",fontSize:13 }}>Annulla</button>
            <button disabled={addLineSaving||!addLineForm.phaseId||!addLineForm.startDate}
              onClick={async()=>{
                setAddLineSaving(true);
                try {
                  await fetchJson(`/postplans/${planId}/support-line`, { method:"POST", body:JSON.stringify({ phaseId:Number(addLineForm.phaseId), label:addLineForm.label||null, startDate:addLineForm.startDate, endDate:addLineForm.endDate||null }) });
                  setShowAddLine(false);
                  setAddLineForm({ phaseId:'', label:'', startDate:'', endDate:'' });
                  refreshPlan();
                } catch(e) { alert('Errio: '+e.message); }
                finally { setAddLineSaving(false); }
              }}
              style={{ background:"linear-gradient(135deg,#d4a550,#b8892f)",border:"none",borderRadius:6,color:"#0a0a0f",padding:"8px 20px",cursor:"pointer",fontSize:13,fontWeight:800,opacity:(addLineSaving||!addLineForm.phaseId||!addLineForm.startDate)?0.4:1 }}>
              {addLineSaving ? "Aggiunta…" : "Aggiungi"}
            </button>
          </div>
        </div>
      </div>
    )}

    </div>
  );
}